import { Link } from '@remix-run/react';
import { json, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import {
  ArrowRightIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import Header from '~/components/Header';
import Footer from '~/components/Footer';
import {
  generateCanonicalUrl,
  generateBreadcrumbJsonLd,
  SITE_NAME,
} from '~/utils/seo';
import FAQ from '~/components/FAQ';
import { BLOG_CACHE_HEADERS } from '~/utils/cacheHeaders';
import { getAuthenticatedUser } from '~/utils/supabase.server';

export const maxDuration = 300;

const SLUG = '6-ways-to-connect-social-media-to-your-marketing-stack';
const TITLE = '6 Ways to Connect Social Media to Your Marketing Stack';
const HERO_IMAGE = '/blog/6-ways-to-connect-social-media-to-your-marketing-stack-hero.jpg';
const HERO_IMAGE_WEBP = '/blog/6-ways-to-connect-social-media-to-your-marketing-stack-hero.webp';
const HERO_IMAGE_AVIF = '/blog/6-ways-to-connect-social-media-to-your-marketing-stack-hero.avif';
const STACK_IMAGE =
  'https://cdn.imgcloud.org/content/qs7cadnrctrwzm2ycsxv1kba1x8hc48w/6-ways-to-connect-social-media-to-your-marketing-stack-stack-setup-order.jpg';
const HUB_IMAGE = 'https://cdn.imgcloud.org/screenshots/thetabber_com_6c2e7faa0051.png';

export const meta: MetaFunction = () => {
  const canonicalUrl = generateCanonicalUrl(`/blog/${SLUG}`);
  const metaTitle = '6 Ways to Connect Social Media to Your Marketing Stack';
  const description =
    'Connect social media to your marketing stack: publish from one hub, tag every link, route social leads into your CRM, feed engagement into profiles, and bridge the gaps.';

  return [
    { title: `${metaTitle} | ${SITE_NAME}` },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'marketing automation social media integration, connect social media to marketing stack, social media CRM integration, UTM campaign tagging, social leads to CRM, Klaviyo social marketing, marketing stack integration',
    },
    { property: 'og:title', content: `${metaTitle} | ${SITE_NAME}` },
    { property: 'og:description', content: description },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: generateCanonicalUrl(HERO_IMAGE) },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: `${metaTitle} | ${SITE_NAME}` },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: generateCanonicalUrl(HERO_IMAGE) },
    { tagName: 'link', rel: 'canonical', href: canonicalUrl },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthenticatedUser(request);

  return json({ isLoggedIn: !!user }, { headers: BLOG_CACHE_HEADERS });
}

const FAQS = [
  {
    question: 'What is marketing automation social media integration?',
    answer:
      'It is the set of connections that carry social activity into the rest of your marketing systems. Social leads land in your CRM, engagement updates customer profiles, and tagged links show up in web analytics and revenue reports. Scheduling is part of it, but the integration is what happens after publishing: which fields move, which workflows trigger, and which system owns each record, so you can tie a post to a sale.',
  },
  {
    question: 'Are free schedulers and native platform tools good enough?',
    answer:
      'They are good enough for posting and not good enough for connecting. Buffer\'s free plan covers three channels, and native schedulers inside each platform cost nothing. Each one keeps its own records, though, so your campaign names and links scatter across apps. Once you need to report revenue or route leads, a single publishing hub with consistent tagging saves more time than it costs.',
  },
  {
    question: 'Which social media tasks should not be automated?',
    answer:
      'Sensitive replies, complaints and anything resembling a crisis should reach a human. Automate scheduling, cross-posting, link tagging, lead capture and data syncing. Keep judgment calls manual: tone in public replies, apologies and deciding whether a viral comment needs a response. Tools like Hootsuite\'s Nest exist for routing these conversations to people instead of bots.',
  },
  {
    question: 'How do I measure revenue from social media?',
    answer:
      'Start with one campaign and consistent UTM tags, as Sprout Social recommends, rather than rebuilding all your attribution at once. Use the same campaign value in your links, email platform and CRM records, then follow tagged visitors through signup and purchase. Once one campaign reports cleanly, repeat the taxonomy across the rest.',
  },
  {
    question: 'Can I collect emails from social DMs and comments?',
    answer:
      'Yes, if you collect explicit consent and keep a record of it. Klaviyo Social Marketing, generally available since June 30, 2026, uses auto-replies to gather email, SMS or WhatsApp consent from social conversations. Whatever tool you use, document what you collect, where the consent record lives and who owns the data before it enters any journey.',
  },
];

const SETUP_STEPS = [
  'Name a system of record for each data type: contacts, campaign metadata, consent, engagement events and revenue. Write it down in one shared document.',
  'Move all publishing into one hub, and retire native schedulers for anything tied to a campaign.',
  'Agree on a UTM taxonomy, lowercase and hyphenated, and apply it to one upcoming campaign first.',
  'Map every social lead-form field to a named CRM field, including source network and campaign.',
  'Write the follow-up workflow before you switch on lead capture.',
  'Record how consent is collected, and confirm it travels with the contact into email, SMS and ad audiences.',
  'Test each integration for field mapping errors, sync failures, duplicate records, permission scopes and refresh timing.',
  'Set a monthly 20-minute check for sync errors and duplicates, and assign it to one named person.',
];

const GLANCE_ROWS: [string, string, string][] = [
  ['One publishing hub (Thetabber)', 'Creators and small teams posting to several platforms', 'See site'],
  ['Consistent campaign tags (UTMs)', 'Anyone who has to show revenue from social', 'Free, setup time only'],
  ['Social leads into the CRM', 'Gated content, giveaways, lead-form offers', 'Your existing CRM plan'],
  ['Engagement into customer profiles', 'Ecommerce and lifecycle email or SMS', 'Klaviyo free up to 250 profiles; paid varies'],
  ['Automated ad audiences', 'Brands with regular paid social spend', 'Ad spend plus connector costs'],
  ['API and workflow bridges', 'Gaps with no native connector', 'Varies by workflow tool'],
];

const extLink = 'text-gray-900 underline hover:text-gray-700';

export default function ConnectSocialMediaToMarketingStackPage() {
  const { isLoggedIn } = useLoaderData<typeof loader>();

  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: 'Home', url: generateCanonicalUrl('/') },
    { name: 'Blog', url: generateCanonicalUrl('/blog') },
    {
      name: '6 Ways to Connect Social Media to Your Marketing Stack',
      url: generateCanonicalUrl(`/blog/${SLUG}`),
    },
  ]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: TITLE,
    description:
      'Six ways to connect social media to your marketing stack, ranked by which handoff unblocks the others: one publishing hub, UTM tagging, CRM routing, engagement profiles, ad audiences, and API bridges.',
    image: generateCanonicalUrl(HERO_IMAGE),
    datePublished: '2026-11-02',
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: generateCanonicalUrl('/'),
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />

      <Header variant="static" isLoggedIn={isLoggedIn} />

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center hover:text-gray-700 transition-colors">
            <HomeIcon className="w-4 h-4 mr-1" />
            Home
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <Link to="/blog" className="hover:text-gray-700 transition-colors">
            Blog
          </Link>
          <ChevronRightIcon className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Connect Social to Your Marketing Stack</span>
        </nav>
      </div>

      {/* Hero Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.4]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(156 163 175) 1px, transparent 0)`,
            backgroundSize: '24px 24px',
            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
            WebkitMaskImage:
              'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 mb-6">
              <LightBulbIcon className="w-5 h-5 text-gray-700" />
              <span className="text-sm font-semibold text-gray-700">Marketing Stack</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6 leading-tight">
              {TITLE}
            </h1>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Open the analytics for last month's best Instagram Reel and you will find views, saves
              and maybe a count of profile visits. Now try to find that same Reel in your email list,
              your CRM, or the revenue summary you send a sponsor. For most small teams, the trail
              stops at the platform. Connecting social media to your marketing stack means fixing that.
            </p>

            <p className="mt-6 text-sm text-gray-500">Nov 2, 2026 · 12 min read</p>
          </div>
        </div>
      </section>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <figure className="mb-12 not-prose">
          <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
            <picture>
              <source srcSet={HERO_IMAGE_AVIF} type="image/avif" />
              <source srcSet={HERO_IMAGE_WEBP} type="image/webp" />
              <img
                src={HERO_IMAGE}
                alt="6 ways to connect social media to your marketing stack"
                className="w-full"
              />
            </picture>
          </div>
        </figure>

        <article className="prose prose-lg max-w-none">
          <p className="text-lg text-gray-600 mb-6 leading-relaxed">
            Every post should carry data forward: a campaign tag your web analytics can read, a lead
            that reaches the CRM with its source still attached, an engagement signal that can start
            an email or SMS journey. Marketing automation and social media integration only pays off
            when those handoffs exist.
          </p>
          <p className="text-gray-600 mb-10 leading-relaxed">
            Our top pick is the least glamorous one: run every post through a single publishing hub
            before you wire anything else. We built Thetabber for that job. Integration problems start
            when content leaves from five different apps with five different naming habits. According
            to{' '}
            <a
              href="https://blog.hubspot.com/marketing/hubspot-blog-social-media-marketing-report"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              HubSpot's 2026 State of Social Media Marketing Report
            </a>
            , which surveyed more than 1,100 marketers, only 36% describe their stack as very cohesive
            or fully integrated. The other two-thirds are working with posts that lead nowhere.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">The short version</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            To connect social media to your marketing stack, publish from one hub, tag every link with
            consistent campaign parameters, send social leads into your CRM with the network and
            campaign preserved, and feed consented engagement into customer profiles. After that,
            automate ad audiences and use workflow tools or APIs to cover what native connectors miss.
            Start with one campaign rather than a full measurement overhaul.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">How we ranked these</h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            I ranked each method by where social data goes after a post goes live. A method ranks
            higher when it unblocks the others, when a team of one to ten people can set it up without
            hiring an integrations specialist, and when it produces a record another system can read.
            That last test explains the order. Campaign tags are useless if posts leave from four
            apps. CRM routing is guesswork without tags. Audience automation needs clean profiles
            first. The list follows the order you should build in.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            One publishing hub for every platform
          </h2>

          <figure className="my-8 not-prose">
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
              <img src={HUB_IMAGE} alt="Thetabber publishing hub" className="w-full" />
            </div>
          </figure>

          <p className="text-gray-600 mb-6 leading-relaxed">
            Thetabber is social media publishing software that sends posts to 9+ social platforms from
            one calendar. That is the reason it opens this list: once every post starts in the same
            place, every post can carry the same campaign name, the same link conventions and the same
            approval history. If you launch a course, a product or a sponsored series, the hub becomes
            your shared campaign calendar. Email, landing pages and paid media can be planned against
            it, so nobody has to rebuild the schedule from three native apps and a spreadsheet.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Small teams often say their current setup is simple enough: a free Buffer plan, a
            spreadsheet and the native schedulers inside each platform. That setup works until you need
            the data to go somewhere else. Native schedulers keep their records inside each platform. A
            spreadsheet is a calendar no other system can read. The real cost is not the subscription
            you avoided. It is the Sunday evening spent copying captions and checking whether Tuesday's
            TikTok went out. That complaint comes up again and again on r/DigitalMarketing, where
            businesses describe automation mainly as a way to stop reposting the same content by hand
            across several accounts. The same users also say they want no extra complexity for small
            accounts. On the{' '}
            <Link to="/features" className="text-gray-900 font-semibold hover:underline">
              features page
            </Link>{' '}
            you can see how scheduling, multi-platform publishing and repurposing sit in one workspace.
            Plans are listed on our{' '}
            <Link to="/pricing" className="text-gray-900 font-semibold hover:underline">
              pricing page
            </Link>
            .
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            The competitors here do good work for particular readers. Buffer charges per channel. The
            Free plan covers up to three channels. Essentials costs $6 per channel per month, or $5
            billed annually ($60 a year per channel), for a single user, and Team costs $12 per
            channel, or $10 annually, and adds unlimited members. A creator on five channels at annual
            Team rates pays about $50 a month, which is fair for its collaboration tools. Later suits brands that plan visually and depend on a
            link-in-bio page. SocialBee's category-based queues and evergreen recycling keep a posting
            cadence alive with little effort. Publer handles multi-network scheduling and collaboration
            well.
          </p>

          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-5 my-6 not-prose">
            <LightBulbIcon className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-700 text-sm leading-relaxed">
              <span className="font-semibold text-gray-900">Tip: </span>
              Before choosing any hub, write down where its data has to go next. If it has no export,
              API or automation hook, it will become another dead-end tool, however good its calendar
              looks.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Tag every post so revenue reports can find it
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            A UTM tag is a set of{' '}
            <a
              href="https://en.wikipedia.org/wiki/UTM_parameters"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              parameters added to a URL
            </a>{' '}
            that tells your web analytics where a visitor came from. It is the cheapest integration on
            this list and the one most teams apply inconsistently. Here is a typical failure: one
            person writes "IG", another writes "instagram", a third forgets the tag, and the launch
            report splits into three rows that never add up.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            I call the fix campaign-name discipline. Pick one lowercase taxonomy and enforce it from
            the hub. A workable pattern is <code>utm_source=instagram</code>,{' '}
            <code>utm_medium=social</code>, <code>utm_campaign=spring-course-launch-2026</code>, with{' '}
            <code>utm_content</code> naming the specific post or creative. When the same campaign value
            appears in your analytics, your email platform and your CRM's deal records, you can follow
            one post through a visit, a signup and a sale.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            This matters because most teams still measure social inside social tools. Sprout Social's
            2025 Impact of Social Media report found that 55% of respondents use social media
            management software to measure performance. Only 27% use Salesforce for that, and 24% use
            HubSpot. Social numbers stay in the social tab while revenue lives elsewhere. In the{' '}
            <a
              href="https://media.sproutsocial.com/uploads/2025/08/Sprout-Social-2025-Impact-of-Social-Media-Report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              same Sprout survey of 1,200 marketing leaders
            </a>
            , more than half named incompatibility between social tools and the rest of the stack as
            the top reason they cannot see social's business impact.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Metricool is a reasonable companion if you want social scheduling and cross-channel
            reporting in one dashboard. Still, take Sprout's advice and attribute one campaign end to
            end before you try to tag your entire back catalog.
          </p>

          <figure className="my-10 not-prose">
            <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-lg">
              <img
                src={STACK_IMAGE}
                alt="Build your stack in order: assign systems of record for each data type, centralize campaign publishing in one hub, standardize UTM tagging, route leads to the CRM, feed engagement into profiles, then automate audiences and bridges."
                className="w-full"
              />
            </div>
          </figure>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Route social leads into the CRM with their source intact
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Most marketers already have the destination. The MarTech 2025 State of Your Stack Survey
            (169 organizations) counted 86.4% using a CRM and 76.9% using marketing automation. The gap
            is the path between them and social. Sprout found that fewer than half of marketing leaders
            embed social data in any CRM. A lead from a LinkedIn lead form or an Instagram giveaway
            usually arrives as a CSV export, if it arrives at all.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            A working social-to-CRM connection does three things. First, it maps form fields to CRM
            fields one by one: email, first name, and any qualifying question you asked. Second, it
            keeps the originating network and campaign as their own fields, for example{' '}
            <code>lead_source_network</code> and <code>lead_campaign</code>, so they are not buried in
            a notes box. Third, it triggers a follow-up workflow defined in advance, such as a
            three-email welcome sequence or a task assigned to whoever handles sales calls.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            That third step is where most setups fail. B2B content marketers on r/content_marketing
            keep asking how to move gated-content leads further down the funnel. Their frustration
            makes the point well: a captured email with no follow-up is a contact you will never sell
            to. If you sell a course or coaching offer, write the follow-up sequence before you connect
            the form.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Feed engagement into customer profiles and journeys
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Comments, DMs and replies are the richest social signals and the riskiest to automate.
            Klaviyo makes the strongest case here. It{' '}
            <a
              href="https://www.klaviyo.com/newsroom/klaviyo-social-marketing-ga"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              announced Klaviyo Social Marketing in July 2026
            </a>{' '}
            and made it generally available from June 30, 2026. Its social auto-replies collect email,
            SMS or WhatsApp consent and customer data from conversations, then pass it into Klaviyo
            profiles. For an ecommerce brand already on Klaviyo, this moves a comment straight into a
            lifecycle flow. The free tier covers 250 active profiles and 500 email sends a month,
            mobile messages cost $5 a month, and paid pricing scales with profiles and sending volume.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Treat consent as a design requirement. Before a reaction or DM affects an email, SMS, ad or
            support journey, document what you collect, why, who owns the data, and which system holds
            the consent record.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Hootsuite takes the enterprise route to the same idea. Its 2026 lineup splits into Perch
            for publishing and planning, Nest for customer care, Parliament for employee advocacy and
            Lumen by Talkwalker for social intelligence. Nest handles reply routing and
            human-in-the-loop care, which matters if a crisis can start in your comments. Pricing
            starts at $99 for Standard with up to 10 profiles, rises to $199 for Professional with
            unlimited profiles and more automation and reporting, then $399 for Advanced, with
            Enterprise quoted separately (
            <a
              href="https://www.hootsuite.com/plans"
              target="_blank"
              rel="noopener noreferrer"
              className={extLink}
            >
              Hootsuite plans
            </a>
            ). That makes sense for a care team. For a three-person creator business, it is more tool
            than the job needs.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Ad audiences and API bridges: the two ways to cover the gaps
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            These last two methods belong together because both cover data that native connectors do
            not handle. Automating audience and ad workflows means syncing CRM segments, such as course
            buyers or webinar no-shows, into paid social audiences so ads either exclude or target
            them. It pays off once you spend regularly on Meta or LinkedIn ads. Before that, building it
            is premature.
          </p>
          <p className="text-gray-600 mb-6 leading-relaxed">
            Bridges are the vendor-neutral option. Zapier-style workflow automation, or a direct API,
            can connect social platforms to CRMs, analytics, email and ad audiences when no native
            integration exists. Thetabber exposes{' '}
            <Link to="/agents" className="text-gray-900 font-semibold hover:underline">
              an MCP server and social media API for AI agents
            </Link>
            , so publishing can be triggered from the workflows you already run. These skills are now in
            demand: posts on r/SocialMediaManagers describe growing need for people who combine social
            expertise with workflow builders and AI agents. Implementation skill is part of the
            purchase, whether you hire it or learn it. With 48% of marketers in HubSpot's report
            expecting their stack to grow this year, more bridges are coming, and every bridge needs
            monitoring.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">
            Before you connect anything: a setup order
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed">
            This sequence keeps a small team from building integrations that break quietly:
          </p>
          <ol className="space-y-3 mb-6 list-decimal list-inside not-prose">
            {SETUP_STEPS.map((step, i) => (
              <li key={i} className="text-gray-700 leading-relaxed">
                {step}
              </li>
            ))}
          </ol>

          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-5 my-6 not-prose">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-700 text-sm leading-relaxed">
              <span className="font-semibold text-gray-900">Warning: </span>
              Duplicate contacts are the quiet failure. If a social lead form and your email signup
              both create records, decide which system matches on email address before either goes
              live.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-12">The six ways at a glance</h2>
          <div className="overflow-x-auto my-8 not-prose">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 pr-4 font-semibold text-gray-900">Way to connect</th>
                  <th className="py-3 pr-4 font-semibold text-gray-900">Best for</th>
                  <th className="py-3 font-semibold text-gray-900">Rough cost</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {GLANCE_ROWS.map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 align-top">
                    <td className="py-3 pr-4 font-medium text-gray-900">{row[0]}</td>
                    <td className="py-3 pr-4">{row[1]}</td>
                    <td className="py-3">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-600 mb-6 leading-relaxed">
            Some options did not make the list. Enterprise suites like Sprinklr are built for large
            care and listening teams, and that is a different problem from the one most growing teams
            have. Content-generation tools like Blaze AI help you produce posts but do not connect them
            to anything downstream. Here is my recommendation, and I would defend it to any founder
            doing their own publishing between client calls: consolidate publishing and fix your
            campaign tags this month, then wire the CRM. Those two steps fix most dead-end posts at
            almost no cost, and every later integration depends on them. If you want help mapping your
            current setup,{' '}
            <Link to="/contact" className="text-gray-900 font-semibold hover:underline">
              our team is reachable here
            </Link>
            .
          </p>
        </article>

        <FAQ items={FAQS} align="left" className="mt-12" innerClassName="" />

        <section className="bg-gray-900 text-white rounded-2xl p-8 lg:p-12 mt-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Publish every campaign from one hub</h2>
          <p className="text-lg text-gray-300 mb-6">
            Start every post in the same place, carry the same campaign name everywhere, and publish to
            9+ platforms from a single screen with TheTabber. Try it free for 3 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
              >
                Go to Dashboard
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            ) : (
              <>
                <Link
                  to="/auth/email/signup"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Start Free Trial
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
                <Link
                  to="/blog"
                  className="bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-700 transition-all"
                >
                  Read More Guides
                </Link>
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
